import { Fragment, ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import FileTitle from "files/components/FileTitle";
import { DeleteFileDialog } from "files/components/ui/delete-file-dialog";
import { FileActionDialog } from "files/components/ui/file-action-dialog";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import {
  BASE_DIRECTORIES,
  encodeObjectKey,
  resolveObjectKey,
  resolveRouterPath,
  useCurrentPath,
} from "files/hooks/useFiles";
import { useFilesProjectId } from "files/hooks/useProjectFilesContext";
import BaseDirectory from "files/types/enums/baseDirectory";
import { JobType } from "files/types/interfaces/fileJob";
import { ChevronDown, Copy, Trash2 } from "lucide-react";
import { BooleanParam, useQueryParam } from "use-query-params";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useUser from "~/contexts/user";
import useIsWorkbenchFileContext from "~/pages/files/hooks/useIsWorkbenchFileContext";
import { CreateFolderDialog } from "~/pages/manifold-nextgen/components/files/CreateFolderDialog";
import { UploadFileDialog } from "~/pages/manifold-nextgen/components/files/UploadFileDialog";

const PROJECT_PREFIX_REGEX = new RegExp(`^${BaseDirectory.PROJECTS}/\\d+/?$`);

interface CrumbProps {
  title: ReactNode;
  prefix: string;
}

// The current folder is a dropdown of the same actions the legacy MUI
// breadcrumb's `MenuCrumb` exposes (New Folder, Upload, copy/rename/move/
// delete), rebuilt on shadcn's `DropdownMenu` — the dialogs it opens stay the
// MUI ones, same compromise `DatasetFiles.tsx` makes for the same reason.
const CurrentFolderMenu = ({ title, prefix }: CrumbProps) => {
  const [, copy] = useCopyToClipboard();
  const basePath = useBasePath();
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const canWrite = useFileAccess(prefix, "write");
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const routerPath = encodeObjectKey(resolveRouterPath(prefix, isGlobalFileContext, datasetId));
  const canModify =
    canWrite && !BASE_DIRECTORIES[routerPath] && !PROJECT_PREFIX_REGEX.test(routerPath);
  const [fileAction, setFileAction] = useState<JobType | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleCopyLink = () => {
    const url = new URL(`${basePath}/${routerPath}`, window.location.origin);
    copy(url.toString());
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-[30px] cursor-pointer items-center gap-1 rounded-sm px-2 text-sm font-semibold text-foreground hover:bg-accent"
          >
            {title}
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
          {canWrite && (
            <DropdownMenuItem onClick={() => setNewFolderOpen(true)}>New Folder</DropdownMenuItem>
          )}
          {canWrite && (
            <DropdownMenuItem onClick={() => setUploadOpen(true)}>Upload Files...</DropdownMenuItem>
          )}
          {canWrite && <DropdownMenuSeparator />}
          <DropdownMenuItem onClick={() => setFileAction(JobType.COPY)}>
            Copy Folder...
          </DropdownMenuItem>
          {canModify && (
            <DropdownMenuItem onClick={() => setFileAction(JobType.RENAME)}>
              Rename...
            </DropdownMenuItem>
          )}
          {canModify && (
            <DropdownMenuItem onClick={() => setFileAction(JobType.MOVE)}>Move...</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleCopyLink}>
            <Copy />
            Copy Link
          </DropdownMenuItem>
          {canModify && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateFolderDialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} />
      <UploadFileDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      {fileAction && (
        <FileActionDialog
          isDirectory
          fileKey={prefix}
          open={!!fileAction}
          action={fileAction}
          handleOpenChange={(open) => !open && setFileAction(null)}
        />
      )}
      <DeleteFileDialog
        isDirectory
        fileKey={prefix.endsWith("/") ? prefix : `${prefix}/`}
        open={deleteOpen}
        handleClose={(deleted) => {
          setDeleteOpen(false);
          if (deleted) {
            navigate("..", { relative: "path" });
          }
        }}
      />
    </>
  );
};

/**
 * Shadcn rebuild of `files/components/FileBreadcrumbs`'s breadcrumb trail, for
 * the nextgen-embedded Files tab only — mirrors its path-segment logic
 * exactly, just with `Breadcrumb`/`DropdownMenu` markup instead of MUI
 * `Breadcrumbs`/`Menu`. The standalone Research Workbench app keeps the
 * original.
 */
export const EmbeddedFileBreadcrumbs = () => {
  const user = useUser();
  const navigate = useNavigate();
  const basePath = useBasePath();
  const filePath = useCurrentPath();
  const projectId = useFilesProjectId();
  const [preview] = useQueryParam("preview", BooleanParam);
  const splitPath = filePath
    .split("/")
    .filter(String)
    .slice(0, preview ? -1 : undefined);

  const breadcrumbs = ["", ...splitPath].map((segment, index) => {
    const topDirectory = index === 0;
    const currentKey = resolveObjectKey(
      splitPath.slice(0, index).join("/"),
      user?.email,
      projectId
    );
    const routerPath = encodeObjectKey([basePath, ...splitPath.slice(0, index)].join("/"));
    const label = topDirectory ? "Files" : <FileTitle fileKey={currentKey} />;

    return { title: label, prefix: currentKey, href: routerPath };
  });

  // At the root, this is a page header, matching DatasetFiles.tsx's own root
  // case — a breadcrumb trail with nothing to drill back out of isn't one.
  if (breadcrumbs.length === 1) {
    return <h1 className="text-2xl font-semibold">{breadcrumbs[0].title}</h1>;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList className="flex-nowrap">
        {breadcrumbs.map((crumb, index) => (
          <Fragment key={crumb.href}>
            {index > 0 && <BreadcrumbSeparator />}
            <BreadcrumbItem>
              {index === breadcrumbs.length - 1 ? (
                <CurrentFolderMenu prefix={crumb.prefix} title={crumb.title} />
              ) : (
                <BreadcrumbLink
                  href={crumb.href}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(crumb.href);
                  }}
                >
                  {/* Matches DatasetFiles.tsx's own root crumb label. */}
                  {index === 0 ? "All Files" : crumb.title}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
